import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastCtx = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
};

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, closing: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 260);
  }, []);

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, type, message, closing: false }]);
    if (duration > 0) setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  /* Memoize the toast API object so consumers (useCallback / useEffect deps)
     get a stable reference and don't re-trigger on every ToastProvider render. */
  const toast = useMemo(() => ({
    success: (msg, dur) => addToast('success', msg, dur),
    error:   (msg, dur) => addToast('error', msg, dur),
    info:    (msg, dur) => addToast('info', msg, dur),
  }), [addToast]);

  const icons = {
    success: <CheckCircle size={16} />,
    error:   <AlertCircle size={16} />,
    info:    <Info size={16} />,
  };

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="em-toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`em-toast em-toast-${t.type} ${t.closing ? 'closing' : ''}`}>
            <div className="em-toast-icon">{icons[t.type]}</div>
            <div className="em-toast-body">{t.message}</div>
            <button className="em-toast-close" onClick={() => removeToast(t.id)}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
};
